import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import { FC, ReactNode, useMemo, useState } from 'react'
import {
  FaBoxOpen,
  FaChartBar,
  FaCheckCircle,
  FaClipboardList,
  FaSearch,
  FaStore,
  FaSyncAlt,
  FaUser
} from 'react-icons/fa'

import {
  AlteredSku,
  filterAlteredSkus,
  getPercentualAlteracao,
  JUSTIFICATIVA_PERSONALIZADA,
  PREDEFINED_JUSTIFICATIONS
} from '@helper/mrp/justifications'
import { formatLotes } from '@helper/mrp/lotes'

export interface ILotesSemRecomendacao {
  loteMultiplo: number | null
  quantidadeLotes: number | null
}

interface Props {
  alteredSkus: AlteredSku[]
  onUpdateJustification: (
    skuId: number,
    justificativa: string,
    observacao: string
  ) => void
  // Exibe a coluna de % de alteração (ou lotes, para itens sem recomendação)
  showPercent?: boolean
  lotesSemRecomendacao?: Map<number, ILotesSemRecomendacao>
  // Quando informado, exibe a coluna de aprovador
  getApproverName?: (sku: AlteredSku) => ReactNode
}

const formatPercent = (value: number) =>
  value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

// Tabela de SKUs alterados com busca, seleção e aplicação de justificativa em massa
const JustificationTable: FC<Props> = ({
  alteredSkus,
  onUpdateJustification,
  showPercent = false,
  lotesSemRecomendacao,
  getApproverName
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [selectedJustification, setSelectedJustification] = useState('')
  const [customJustification, setCustomJustification] = useState('')

  const filteredSkus = useMemo(
    () => filterAlteredSkus(alteredSkus, searchTerm),
    [alteredSkus, searchTerm]
  )

  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems])

  const selectAll =
    filteredSkus.length > 0 &&
    filteredSkus.every(sku => selectedSet.has(sku.id))

  const handleSelectAll = () => {
    setSelectedItems(selectAll ? [] : filteredSkus.map(sku => sku.id))
  }

  const handleSelectItem = (skuId: number) => {
    setSelectedItems(prev =>
      prev.includes(skuId) ? prev.filter(id => id !== skuId) : [...prev, skuId]
    )
  }

  const handleApplyJustification = () => {
    const justificationToApply =
      selectedJustification === JUSTIFICATIVA_PERSONALIZADA
        ? customJustification
        : selectedJustification

    if (!justificationToApply.trim()) return

    const itemsToUpdate =
      selectedItems.length > 0 ? selectedItems : filteredSkus.map(sku => sku.id)

    itemsToUpdate.forEach(skuId => {
      const sku = alteredSkus.find(s => s.id === skuId)
      if (sku) {
        onUpdateJustification(
          skuId,
          justificationToApply.trim(),
          sku.observacao
        )
      }
    })

    setSelectedItems([])
    setSelectedJustification('')
    setCustomJustification('')
  }

  const renderPercent = (sku: AlteredSku) => {
    // Itens sem recomendação: exibe lotes no lugar do % (que seria 99999%)
    const lotes = lotesSemRecomendacao?.get(sku.id)
    if (lotes) {
      return (
        <Box
          p={2}
          bg="purple.100"
          borderRadius="md"
          border="2px solid"
          borderColor="purple.300"
        >
          <Text fontSize="sm" fontWeight="bold" color="purple.700">
            {formatLotes(lotes.quantidadeLotes, lotes.loteMultiplo)}
          </Text>
        </Box>
      )
    }

    const percentual = getPercentualAlteracao(sku)
    const isPositive = percentual > 0
    const isZero = percentual === 0
    const color = isZero ? 'gray' : isPositive ? 'green' : 'red'

    return (
      <Box
        p={2}
        bg={`${color}.100`}
        borderRadius="md"
        border="2px solid"
        borderColor={`${color}.300`}
      >
        <Text fontSize="sm" fontWeight="bold" color={`${color}.700`}>
          {isPositive ? '+' : ''}
          {formatPercent(percentual)}%
        </Text>
      </Box>
    )
  }

  const headerCell = (label: string, icon?: ReactNode, isNumeric = false) => (
    <Th
      color="white"
      fontSize="xs"
      fontWeight="bold"
      py={4}
      isNumeric={isNumeric}
    >
      <HStack spacing={2}>
        {icon}
        <Text>{label}</Text>
      </HStack>
    </Th>
  )

  return (
    <VStack spacing={6} align="stretch">
      <Box
        p={6}
        bg="white"
        borderRadius="lg"
        border="2px solid"
        borderColor="green.100"
        boxShadow="md"
      >
        <VStack spacing={4} align="stretch">
          <FormControl id="justification-search">
            <FormLabel fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
              <HStack spacing={2}>
                <FaSearch />
                <Text>Filtrar Produtos</Text>
              </HStack>
            </FormLabel>
            <Input
              placeholder="Digite para filtrar por produto, ID ou loja..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              bg="white"
              borderColor="gray.300"
              focusBorderColor="green.500"
              size="md"
              boxShadow="sm"
              _hover={{ borderColor: 'gray.400' }}
            />
          </FormControl>

          <Box
            p={4}
            bg="blue.50"
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
          >
            <Flex wrap="wrap" gap={6} align="end">
              <FormControl minW="250px" w="auto">
                <FormLabel
                  fontSize="sm"
                  fontWeight="bold"
                  color="blue.700"
                  mb={3}
                >
                  <HStack spacing={2}>
                    <FaCheckCircle />
                    <Text>Controle de Seleção</Text>
                  </HStack>
                </FormLabel>
                <Box p={3} bg="white" borderRadius="md" boxShadow="sm">
                  <Checkbox
                    isChecked={selectAll}
                    onChange={handleSelectAll}
                    colorScheme="green"
                    size="md"
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      Selecionar todos visíveis ({filteredSkus.length} itens)
                    </Text>
                  </Checkbox>
                </Box>
              </FormControl>

              <FormControl id="justification-default" minW="350px" w="auto">
                <FormLabel
                  fontSize="sm"
                  fontWeight="bold"
                  color="blue.700"
                  mb={3}
                >
                  <HStack spacing={2}>
                    <FaClipboardList />
                    <Text>Justificativa Padrão</Text>
                  </HStack>
                </FormLabel>
                <Select
                  placeholder="Selecione uma justificativa..."
                  value={selectedJustification}
                  onChange={e => setSelectedJustification(e.target.value)}
                  bg="white"
                  color="black"
                  borderColor="gray.300"
                  focusBorderColor="green.500"
                  size="md"
                  boxShadow="sm"
                  _hover={{ borderColor: 'gray.400' }}
                  sx={{
                    option: {
                      background: 'white',
                      color: 'black'
                    }
                  }}
                >
                  {PREDEFINED_JUSTIFICATIONS.map(justification => (
                    <option key={justification} value={justification}>
                      {justification}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Button
                bg="linear-gradient(135deg, #059669 0%, #047857 100%)"
                color="white"
                onClick={handleApplyJustification}
                isDisabled={
                  !selectedJustification ||
                  (selectedJustification === JUSTIFICATIVA_PERSONALIZADA &&
                    !customJustification.trim())
                }
                size="md"
                px={6}
                boxShadow="md"
                _hover={{
                  bg: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg'
                }}
                leftIcon={<FaCheckCircle />}
              >
                Aplicar
              </Button>
            </Flex>
          </Box>

          {selectedJustification === JUSTIFICATIVA_PERSONALIZADA && (
            <Box
              p={4}
              bg="orange.50"
              borderRadius="md"
              border="1px solid"
              borderColor="orange.200"
            >
              <FormControl id="justification-custom">
                <FormLabel
                  fontSize="sm"
                  fontWeight="bold"
                  color="orange.700"
                  mb={3}
                >
                  <HStack spacing={2}>
                    <FaClipboardList />
                    <Text>Justificativa Personalizada</Text>
                  </HStack>
                </FormLabel>
                <Textarea
                  placeholder="Digite sua justificativa personalizada..."
                  value={customJustification}
                  onChange={e => setCustomJustification(e.target.value)}
                  rows={3}
                  bg="white"
                  color="black"
                  borderColor="gray.300"
                  focusBorderColor="orange.500"
                  boxShadow="sm"
                  _hover={{ borderColor: 'gray.400' }}
                />
              </FormControl>
            </Box>
          )}

          {selectedItems.length > 0 && (
            <Box
              p={4}
              bg="yellow.50"
              borderRadius="md"
              border="2px solid"
              borderColor="yellow.400"
            >
              <Text fontSize="md" color="yellow.800" fontWeight="bold">
                Seleção Ativa
              </Text>
              <Text fontSize="sm" color="yellow.700">
                {selectedItems.length} item(s) selecionado(s) de{' '}
                {filteredSkus.length} visível(s)
              </Text>
            </Box>
          )}
        </VStack>
      </Box>

      <Box
        borderRadius="lg"
        overflow="hidden"
        boxShadow="lg"
        border="1px solid"
        borderColor="gray.200"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg="linear-gradient(135deg, #6b7280 0%, #4b5563 100%)">
              <Tr>
                <Th color="white" py={4}>
                  <Checkbox
                    isChecked={selectAll}
                    onChange={handleSelectAll}
                    colorScheme="green"
                    size="md"
                    aria-label="Selecionar todos visíveis"
                    sx={{
                      '.chakra-checkbox__control': {
                        border: '2px solid white',
                        _checked: {
                          border: '2px solid white'
                        }
                      }
                    }}
                  />
                </Th>
                {headerCell('PRODUTO', <FaBoxOpen size={14} />)}
                {headerCell('LOJA', <FaStore size={14} />)}
                {headerCell('QTD. RECOMENDADA', <FaChartBar size={14} />, true)}
                {headerCell('NOVA QTD.', <FaSyncAlt size={14} />, true)}
                {showPercent && headerCell('% ALTERAÇÃO / LOTES', null, true)}
                {headerCell('JUSTIFICATIVA', <FaClipboardList size={14} />)}
                {headerCell('OBSERVAÇÃO', <FaClipboardList size={14} />)}
                {getApproverName &&
                  headerCell('APROVADOR', <FaUser size={14} />)}
              </Tr>
            </Thead>
            <Tbody>
              {filteredSkus.map((sku, index) => {
                const isSelected = selectedSet.has(sku.id)
                const isIncrease = sku.novaQuantidade > sku.quantidadeOriginal

                return (
                  <Tr
                    key={sku.id}
                    bg={
                      isSelected
                        ? 'yellow.50'
                        : index % 2 === 0
                        ? 'white'
                        : 'gray.50'
                    }
                    _hover={{ bg: isSelected ? 'yellow.100' : 'blue.50' }}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    <Td py={4}>
                      <Checkbox
                        isChecked={isSelected}
                        onChange={() => handleSelectItem(sku.id)}
                        colorScheme="green"
                        size="md"
                        aria-label={`Selecionar produto ${sku.produto?.id}`}
                      />
                    </Td>
                    <Td py={4}>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800">
                          #{sku.produto?.id}
                        </Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={2}>
                          {sku.produto?.descricao}
                        </Text>
                      </VStack>
                    </Td>
                    <Td py={4}>
                      <Text fontSize="sm" fontWeight="medium" color="blue.700">
                        {sku.loja?.id} - {sku.loja?.descricao}
                      </Text>
                    </Td>
                    <Td isNumeric py={4}>
                      <Box p={2} bg="gray.100" borderRadius="md">
                        <Text fontSize="sm" fontWeight="medium">
                          {sku.quantidadeOriginal.toLocaleString('pt-BR')}
                        </Text>
                      </Box>
                    </Td>
                    <Td isNumeric py={4}>
                      <Box
                        p={2}
                        bg={isIncrease ? 'green.100' : 'red.100'}
                        borderRadius="md"
                        border="2px solid"
                        borderColor={isIncrease ? 'green.300' : 'red.300'}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={isIncrease ? 'green.700' : 'red.700'}
                        >
                          {sku.novaQuantidade.toLocaleString('pt-BR')}
                        </Text>
                      </Box>
                    </Td>
                    {showPercent && (
                      <Td isNumeric py={4}>
                        {renderPercent(sku)}
                      </Td>
                    )}
                    <Td py={4}>
                      <Box
                        p={2}
                        borderRadius="md"
                        border="2px solid"
                        borderColor={sku.justificativa ? 'gray.300' : 'red.300'}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={sku.justificativa ? 'inherit' : 'red.600'}
                        >
                          {sku.justificativa || 'Sem justificativa'}
                        </Text>
                      </Box>
                    </Td>
                    <Td py={4}>
                      <Input
                        size="sm"
                        value={sku.observacao}
                        onChange={e =>
                          onUpdateJustification(
                            sku.id,
                            sku.justificativa,
                            e.target.value
                          )
                        }
                        placeholder="Observação opcional..."
                        aria-label={`Observação do produto ${sku.produto?.id}`}
                        bg="white"
                        borderColor="gray.300"
                        focusBorderColor="blue.500"
                        boxShadow="sm"
                        _hover={{ borderColor: 'gray.400' }}
                      />
                    </Td>
                    {getApproverName && (
                      <Td py={4}>
                        <Text fontSize="xs" color="gray.600">
                          {getApproverName(sku)}
                        </Text>
                      </Td>
                    )}
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <HStack
        spacing={6}
        p={4}
        bg="gray.50"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.300"
      >
        <Box textAlign="center">
          <Text fontSize="xl" fontWeight="bold" color="blue.600">
            {alteredSkus.length}
          </Text>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            TOTAL
          </Text>
        </Box>
        <Box textAlign="center">
          <Text fontSize="xl" fontWeight="bold" color="green.600">
            {filteredSkus.length}
          </Text>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            VISÍVEIS
          </Text>
        </Box>
        <Box textAlign="center">
          <Text fontSize="xl" fontWeight="bold" color="yellow.600">
            {selectedItems.length}
          </Text>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            SELECIONADOS
          </Text>
        </Box>
      </HStack>
    </VStack>
  )
}

export default JustificationTable
